import { FC, useState, useEffect } from "react";
import { WEIGHT_RANGE_ORDERED, WEIGHT_DATA } from "../../constants/foot";
import { useApp } from "../../context/AppContext";
import styles from "../../assets/css/preview.module.scss";
import Personilzation3DModelMain from "./3DModel/Main";
import { useNavigate } from "react-router-dom";
import isEmpty from "lodash/isEmpty";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import { trackPersonalizationStart, trackPersonalizationFormField, trackAddToCart, trackPersonalizedInsoleEnd } from "../../services/google_analytics";
import { sanitizeEngravingText, sanitizeNameField } from "../../helpers/foot";
import { clampToMaxLength } from "../../helpers/validations";
import { useResponsive } from "../../hooks/useResponsive";
import StudioSlot from "../insole-studio/StudioSlot";
import { useStudioSlots } from "../insole-studio/StudioLayout";
import { reportError } from "../../config/newrelic";

const MAX_ENGRAVING_LENGTH = 8;
const MAX_FIRST_NAME_LENGTH = 12;
const MAX_LAST_NAME_LENGTH = 18;

const Personalization: FC = () => {
  useScrollToTop();
  const navigate = useNavigate();
  const {
    setCustomInsoleValues,
    customInsoleValues: { weightRangeData, leftFootEngraving, rightFootEngraving, firstNameOnBox, lastNameOnBox, leftFootArchHeightData, rightFootArchHeightData, leftFootSizeData, rightFootSizeData, leftFootLengthInMM, rightFootLengthInMM, leftFootArchHeightInMM, rightFootArchHeightInMM, scanDataConsent },
    selectedFoamPackageOption,
    productData,
    selectedSizeOption,
    setSelectedSizeOption,
    foamPackagePersonalizationSubText,
  } = useApp();

  const { isMobile } = useResponsive();

  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isMobileFirstPageActive, setIsMobileFirstPageActive] = useState(true);
  const [fullNameOnBox, setFullNameOnBox] = useState(firstNameOnBox + "\n" + lastNameOnBox);

  useEffect(() => {
    trackPersonalizationStart();
  }, []);

  const { setCanvasContainerClass } = useStudioSlots();

  useEffect(() => {
    setCanvasContainerClass(styles.canvasContainerPersonalization);
    return () => setCanvasContainerClass('');
  }, []);

  const handlOnChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    switch (name) {
      case "weightRange": {
        const data = WEIGHT_DATA[value];
        setCustomInsoleValues((prevValues) => ({
          ...prevValues,
          weightRangeData: data,
        }));
        break;
      }
      case "shoeSize": {
        setSelectedSizeOption(value);
        break;
      }
      case "leftFootEngraving":
        {
          const sanitizedValue = clampToMaxLength(sanitizeEngravingText(value, true).trimStart(), MAX_ENGRAVING_LENGTH).toUpperCase();
          setCustomInsoleValues((prevValues) => ({
            ...prevValues,
            leftFootEngraving: sanitizedValue,
          }));
        }
        break;
      case "rightFootEngraving":
        {
          const sanitizedValue = clampToMaxLength(sanitizeEngravingText(value, true).trimStart(), MAX_ENGRAVING_LENGTH).toUpperCase();
          setCustomInsoleValues((prevValues) => ({
            ...prevValues,
            rightFootEngraving: sanitizedValue,
          }));
        }
        break;
      case "firstNameOnBox":
        {
          const sanitizedValue = clampToMaxLength(sanitizeNameField(value).trimStart(), MAX_FIRST_NAME_LENGTH).toUpperCase();
          setCustomInsoleValues((prevValues) => ({
            ...prevValues,
            firstNameOnBox: sanitizedValue,
          }));
          setFullNameOnBox(sanitizedValue + "\n" + lastNameOnBox);
        }
        break;
      case "lastNameOnBox":
        {
          const sanitizedValue = clampToMaxLength(sanitizeNameField(value).trimStart(), MAX_LAST_NAME_LENGTH).toUpperCase();
          setCustomInsoleValues((prevValues) => ({
            ...prevValues,
            lastNameOnBox: sanitizedValue,
          }));
          setFullNameOnBox(firstNameOnBox + "\n" + sanitizedValue);
        }
        break;
      default:
        break;
    }
  };

  const handleFieldBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const trimmed = value.trim();
    switch (name) {
      case "leftFootEngraving":
        setCustomInsoleValues((prev) => ({ ...prev, leftFootEngraving: trimmed }));
        break;
      case "rightFootEngraving":
        setCustomInsoleValues((prev) => ({ ...prev, rightFootEngraving: trimmed }));
        break;
      case "firstNameOnBox":
        setCustomInsoleValues((prev) => ({ ...prev, firstNameOnBox: trimmed }));
        setFullNameOnBox(trimmed + "\n" + lastNameOnBox);
        break;
      case "lastNameOnBox":
        setCustomInsoleValues((prev) => ({ ...prev, lastNameOnBox: trimmed }));
        setFullNameOnBox(firstNameOnBox + "\n" + trimmed);
        break;
    }
  };

  const handleAddToCart = async () => {
    setIsAddingToCart(true);

    try {
      const selectedVariant = productData?.variants.find(
        variant =>
          variant[`option${productData.foamPackageOption?.position}`] === selectedFoamPackageOption &&
          variant[`option${productData.sizeOption?.position}`] === selectedSizeOption
      );

      if (!selectedVariant) {
        console.error('No matching variant found');
        reportError(new Error('No matching variant found'), { context: 'handleAddToCart' });
        setIsAddingToCart(false);
        return;
      }

      const payload = {
        id: selectedVariant.id, // Product variant ID
        quantity: 1,
        properties: {
          "Left Foot Arch Height": 'type' in leftFootArchHeightData ? leftFootArchHeightData.type : '',
          "Right Foot Arch Height": 'type' in rightFootArchHeightData ? rightFootArchHeightData.type : '',
          "_left_foot_arch_height_mm": leftFootArchHeightInMM.toFixed(2),
          "_right_foot_arch_height_mm": rightFootArchHeightInMM.toFixed(2),
          "Left Foot Length": leftFootLengthInMM.toFixed(2),
          "Right Foot Length": rightFootLengthInMM.toFixed(2),
          "Shoe Size": selectedSizeOption,
          "Weight Range": 'label' in weightRangeData ? weightRangeData.label : '',
          "Left Foot Engraving": leftFootEngraving.trim() || "",
          "Right Foot Engraving": rightFootEngraving.trim() || "",
          "Name on Box": `${firstNameOnBox.trim()}\n${lastNameOnBox.trim()}` || "",
          "_first_name_on_box": firstNameOnBox.trim() || "",
          "_last_name_on_box": lastNameOnBox.trim() || "",
          "Scan Data Consent": scanDataConsent ? "true" : "false",
          "_left_reference_cap": `sz${'men' in leftFootSizeData ? leftFootSizeData.men : ''}arc${'bucket' in leftFootArchHeightData ? leftFootArchHeightData.bucket : ''}wt${'bucket' in weightRangeData ? weightRangeData.bucket : ''}`,
          "_right_reference_cap": `sz${'men' in rightFootSizeData ? rightFootSizeData.men : ''}arc${'bucket' in rightFootArchHeightData ? rightFootArchHeightData.bucket : ''}wt${'bucket' in weightRangeData ? weightRangeData.bucket : ''}`,
        }
      };

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const weightLabel = 'label' in weightRangeData ? weightRangeData.label : '';
      trackPersonalizationFormField('weight', weightLabel);
      trackPersonalizationFormField('size', selectedSizeOption);
      trackPersonalizationFormField('left_foot_text', leftFootEngraving);
      trackPersonalizationFormField('right_foot_text', rightFootEngraving);
      trackPersonalizationFormField('box_text', fullNameOnBox);

      trackAddToCart({
        currency: selectedVariant.price_currency || 'USD',
        value: parseFloat(selectedVariant.price),
        items: [{
          item_id: selectedVariant.id.toString(),
          item_name: productData?.title,
          item_brand: productData?.vendor,
          item_category: productData?.product_type,
          item_variant: `${selectedFoamPackageOption} / ${selectedSizeOption}`,
          price: parseFloat(selectedVariant.price),
          quantity: 1,
        }]
      });

      trackPersonalizedInsoleEnd();

      window.location.href = '/cart';
    } catch (error) {
      console.error('Error adding to cart:', error);
      const err = error instanceof Error ? error : new Error(String(error));
      reportError(err, { context: 'handleAddToCart' });
      setIsAddingToCart(false);
    }
  };

  const allSizeOptions = productData?.sizeOptions ?? [];

  // A shoe size is disabled when the variant for the currently selected foam
  // package + that size is out of stock. Variants with unknown availability
  // (fetch failed) are treated as in-stock so we never block purchases on a stale read.
  const outOfStockSizeOptions = productData?.foamPackageOption && productData?.sizeOption
    ? allSizeOptions.filter(size => {
        const variant = productData.variants.find(
          v =>
            (v as unknown as Record<string, unknown>)[`option${productData.foamPackageOption!.position}`] === selectedFoamPackageOption &&
            (v as unknown as Record<string, unknown>)[`option${productData.sizeOption!.position}`] === size
        );
        return variant?.available === false;
      })
    : [];


  // Determine the higher men's size from left and right foot
  const leftMenSize = 'men' in leftFootSizeData ? parseInt(leftFootSizeData.men) : 0;
  const rightMenSize = 'men' in rightFootSizeData ? parseInt(rightFootSizeData.men) : 0;
  const higherMenSize = Math.max(leftMenSize, rightMenSize);

  // Filter size options to show 2 sizes up and 2 sizes down
  const filteredSizeOptions = allSizeOptions.filter(sizeOption => {
    // Extract men's size from the option string
    // Handles: "Men's 5 / Women's 6", "Men's 5 / Women's 6" (curly apostrophe), "M 5 / W 6", etc.
    // Pattern matches: "Men" or "M" followed by any characters (handles different apostrophes/punctuation), then digits
    const match = sizeOption.match(/(Men|M)\S*\s*(\d+)/i);
    if (!match) return false;

    const optionMenSize = parseInt(match[2]);
    return optionMenSize >= higherMenSize - 2 && optionMenSize <= higherMenSize + 2;
  });

  const renderPersonalizationForm = () => {
    const { imageSrc, imageAlt } = productData?.foamPackages.find(foamPackage => foamPackage.name === selectedFoamPackageOption) || {};

    const { title, text } = foamPackagePersonalizationSubText[selectedFoamPackageOption] || {};

    return (
      <div className={`${styles.previewFormBody} ${styles.customScrollbar}`}>
        <div className={styles.formSpacer}>
          <div>
            <div className={styles.previewFormTitleContainer}>
              <h2 className={styles.previewFormTitle}>PERSONALIZE YOUR ME3D</h2>
              <div className={styles.previewFormTitleImgContainer}>
                <img src={imageSrc} alt={imageAlt} className={styles.previewFormTitleImg} />
              </div>
            </div>
            <div className={styles.previewFormSubtitle}>{title} - {text}</div>
          </div>
          {renderFormFields()}
        </div>
      </div>
    );
  };

  const renderFormFields = () => {
    if (isMobile) {
      if (isMobileFirstPageActive) {
        return (
          renderFirstPageFormFields()
        );
      }
      else {
        return (
          renderSecondPageFormFields()
        );
      }
    }

    return (
      <>
        {renderFirstPageFormFields()}
        {renderSecondPageFormFields()}
      </>
    );
  };

  const renderFirstPageFormFields = () => {
    return (
      <>
        <div className={styles.formGroup}>
          <div>
            <div className={styles.previewFormLabelContainer}>
              <h3 className={styles.previewFormLabel}>Approximate Weight</h3>
              <span className={styles.previewFormLabelRequired}>Required</span>
            </div>
            <div className={styles.previewFormLabelDescription}>
              To be able to properly calibrate all the custom parameters, we will
              need to know your approximate weight.
            </div>
          </div>
          <div>
            <select className={styles.formControl} name="weightRange" onChange={handlOnChange}>
              <option value="">Choose Weight</option>
              {WEIGHT_RANGE_ORDERED.map(({ label, lbsRange }) => (
                <option key={lbsRange} value={lbsRange} selected={weightRangeData?.lbsRange == lbsRange}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.formGroup}>
          <div>
            <div className={styles.previewFormLabelContainer}>
              <h3 className={styles.previewFormLabel}>Shoe Size</h3>
              <span className={styles.previewFormLabelRequired}>Required</span>
            </div>
            <div className={styles.previewFormLabelDescription}>
              Choose the size of the shoe you plan on using for your insoles. If you are between sizes or unsure, we recommend sizing half a size up.
            </div>
          </div>
          <div>
            <select className={styles.formControl} name="shoeSize" onChange={handlOnChange}>
              <option value="">Shoe Size</option>
              {filteredSizeOptions.map((size) => {
                const isOutOfStock = outOfStockSizeOptions.includes(size);
                return (
                  <option key={size} value={size} selected={selectedSizeOption === size} disabled={isOutOfStock}>
                    {size}{isOutOfStock ? ' (Out of Stock)' : ''}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
      </>
    );
  };

  const renderSecondPageFormFields = () => {
    return (
      <>
        <div className={styles.formGroup}>
          <div>
            <div className={styles.previewFormLabelContainer}>
              <h3 className={styles.previewFormLabel}>Heel Engraving</h3>
              <span className={styles.previewFormLabelRequired}>Optional</span>
            </div>
            <div className={styles.previewFormLabelDescription}>
             Personalize your ME3D insoles by engraving the heel caps.
            </div>
          </div>
          <div className={styles.grid2colInput}>
            <div>
              <input
                className={styles.formControl}
                name="leftFootEngraving"
                type="text"
                maxLength={MAX_ENGRAVING_LENGTH}
                placeholder="LEFT"
                value={leftFootEngraving}
                onChange={handlOnChange}
                onBlur={handleFieldBlur}
              />
              <div className={`${styles.formCaption} ${styles.mb0} ${styles.mt10}`}>8 characters max</div>
            </div>
            <div>
              <input
                className={styles.formControl}
                name="rightFootEngraving"
                type="text"
                maxLength={MAX_ENGRAVING_LENGTH}
                placeholder="RIGHT"
                value={rightFootEngraving}
                onChange={handlOnChange}
                onBlur={handleFieldBlur}
              />
              <div className={`${styles.formCaption} ${styles.mb0} ${styles.mt10}`}>8 characters max</div>
            </div>
          </div>
        </div>

        <div className={styles.formGroup}>
          <div>
            <div className={styles.previewFormLabelContainer}>
              <h3 className={styles.previewFormLabel}>Name on Box</h3>
              <span className={styles.previewFormLabelRequired}>Optional</span>
            </div>
            <div className={styles.previewFormLabelDescription}>
              Enter your name here so we can personalize your ME3D box.
            </div>
          </div>
          <div>
            <input
              className={styles.formControl}
              name="firstNameOnBox"
              type="text"
              value={firstNameOnBox}
              maxLength={MAX_FIRST_NAME_LENGTH}
              placeholder="FIRST NAME"
              onChange={handlOnChange}
              onBlur={handleFieldBlur}
            />
          </div>
          <div className={styles.formCaption}>12 characters max</div>

          <div>
            <input
              className={styles.formControl}
              name="lastNameOnBox"
              type="text"
              value={lastNameOnBox}
              maxLength={MAX_LAST_NAME_LENGTH}
              placeholder="LAST NAME"
              onChange={handlOnChange}
              onBlur={handleFieldBlur}
            />
          </div>
          <div className={styles.formCaption}>16 characters max</div>
        </div>
      </>
    );
  };

  const renderSubmitButton = () => {
    const isButtonDisabled = isAddingToCart || isEmpty(weightRangeData) || !selectedSizeOption;
    const buttonText = isMobile && isMobileFirstPageActive ? (<><span>Next</span><i className="icon-arrow-right"></i></>) : isAddingToCart ? ('Adding to Cart...') : ('Add to Cart');
    const handleClick = isMobile && isMobileFirstPageActive ? () => setIsMobileFirstPageActive(false) : handleAddToCart;

    return (
      <div>
        <button className={`${styles.btnPill} ${styles.btnPrimary}`} onClick={handleClick} disabled={isButtonDisabled}>
          {buttonText}
        </button>
      </div>
    );
  };

  return (
    <>
      <Personilzation3DModelMain
        leftText={leftFootEngraving}
        rightText={rightFootEngraving}
        selectedFoamPackageOption={selectedFoamPackageOption}
        ribbingLevel={weightRangeData?.bucket}
      />
      <StudioSlot name="rightCol">
        {renderPersonalizationForm()}
      </StudioSlot>
      <StudioSlot name="footer">
        <>
          <div>
            <button className={`${styles.btnPill} ${styles.btnPrimaryOutline}  ${styles.btnCircle}`} onClick={() => navigate(-1)}>
              <i className="icon-arrow-left"></i>
            </button>
          </div>
          {renderSubmitButton()}
        </>
      </StudioSlot>
    </>
  );
};

export default Personalization;
